import { useContext, useMemo, useState } from 'react';

import { Box, Hidden, IconButton, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import { SidebarContext } from 'src/core/contexts/SidebarContext';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useLocation } from 'react-router-dom';

import HeaderMenu from './Menu';
// import HeaderButtons from './Buttons';
import HeaderUserbox from './Userbox';
import { Logo } from 'src/core/components';
import { drHerancaBranding } from 'src/drheranca/branding';
import { useGetInventoryByIdQuery } from 'src/drheranca';

const HeaderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed?: boolean }>(
  ({ theme, collapsed }) => `
        height: ${theme.header.height};
        color: ${drHerancaBranding.color.text.primary};
        padding: ${theme.spacing(0, 2)};
        right: 0;
        z-index: 5;
        background-color: ${drHerancaBranding.color.surface.base};
        box-shadow: none;
        border-bottom: 1px solid ${drHerancaBranding.color.border.default};
        position: fixed;
        justify-content: space-between;
        width: 100%;
        transition: left 0.3s ease;
        @media (min-width: ${theme.breakpoints.values.lg}px) {
            left: ${collapsed ? '80px' : theme.sidebar.width};
            width: auto;
        }
`
);

function Header() {
  const { sidebarToggle, toggleSidebar, sidebarCollapsed, toggleSidebarCollapse } = useContext(SidebarContext);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const location = useLocation();
  const inventoryRouteMatch = location.pathname.match(/^\/inventory\/(?:home|report)\/([^/]+)$/);
  const inventoryId = inventoryRouteMatch?.[1];
  const shouldLoadInventory = Boolean(inventoryId);
  const { data: inventoryApiData } = useGetInventoryByIdQuery(inventoryId as string, {
    skip: !shouldLoadInventory,
    refetchOnMountOrArgChange: true,
  });
  const decujusNome = useMemo(() => {
    if (!shouldLoadInventory) return null;
    if (!inventoryApiData?.json) return null;
    try {
      const parsed = JSON.parse(inventoryApiData.json);
      const nome = parsed?.decujus?.nome;
      return typeof nome === 'string' && nome.trim().length > 0 ? nome.trim() : null;
    } catch {
      return null;
    }
  }, [shouldLoadInventory, inventoryApiData]);

  return (
    <HeaderWrapper className="no-print" display="flex" alignItems="center" collapsed={sidebarCollapsed}>
      <Box display="flex" alignItems="center">
        <Hidden lgUp>
          <Logo />
        </Hidden>
        <Hidden mdDown>
          <HeaderMenu />
        </Hidden>
        <Hidden mdDown>
          {shouldLoadInventory && decujusNome && (
            <Box
              sx={{
                ml: 1.5,
                minWidth: 0,
                alignSelf: 'flex-end',
                pb: 0.5,
                mb: 0
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: drHerancaBranding.color.text.secondary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: { lg: 300, xl: 420 },
                }}
              >
                | <strong style={{ marginLeft: '10px' }}>{decujusNome}</strong>
              </Typography>
            </Box>
          )}
        </Hidden>
        <Hidden lgDown>
          <Tooltip arrow title={sidebarCollapsed ? "Expandir Menu" : "Recolher Menu"}>
            <IconButton
              onClick={toggleSidebarCollapse}
              sx={{
                color: drHerancaBranding.color.text.secondary,
                borderRadius: drHerancaBranding.radius.pill,
                '&:hover': {
                  bgcolor: drHerancaBranding.color.accent.light,
                  color: drHerancaBranding.color.accent.main,
                },
              }}
            >
              {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        </Hidden>
      </Box>
      <Box display="flex" alignItems="center">
        {/* <HeaderButtons /> */}
        <Tooltip arrow title="Ajuda">
          <IconButton
            onClick={() => setIsHelpDialogOpen(true)}
            sx={{
              mr: 1,
              color: drHerancaBranding.color.text.secondary,
              borderRadius: drHerancaBranding.radius.pill,
              '&:hover': {
                bgcolor: drHerancaBranding.color.accent.light,
                color: drHerancaBranding.color.accent.main,
              },
            }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
        <HeaderUserbox />
        <Hidden lgUp>
          <Tooltip arrow title="Abrir Menu">
            <IconButton
              onClick={toggleSidebar}
              sx={{
                color: drHerancaBranding.color.text.secondary,
                borderRadius: drHerancaBranding.radius.pill,
                '&:hover': {
                  bgcolor: drHerancaBranding.color.accent.light,
                  color: drHerancaBranding.color.accent.main,
                },
              }}
            >
              {!sidebarToggle ? <MenuTwoToneIcon /> : <CloseTwoToneIcon />}
            </IconButton>
          </Tooltip>
        </Hidden>
      </Box>
      <Dialog
        open={isHelpDialogOpen}
        onClose={() => setIsHelpDialogOpen(false)}
        aria-labelledby="help-dialog-title"
      >
        <DialogTitle id="help-dialog-title">Ajuda</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Caso precise de suporte, envie um email para "drheranca@gmail.com"
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsHelpDialogOpen(false)} autoFocus>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </HeaderWrapper>
  );
}

export default Header;
